/**
 * How the Reporting page writes times, sizes and counts.
 *
 * Every function takes `now` (and, where it matters, a time zone) as an
 * argument so the same input always reads the same way: a "next run" is only
 * meaningful against a clock, and a test that reads the real one breaks at
 * midnight.
 */

export type ClockOptions = {
  /** The instant to read against. Defaults to the real clock. */
  now?: Date
  /** IANA zone to read in. Defaults to the browser's own. */
  timeZone?: string
  /** Append the zone's short name (EDT, UTC). Used where the zone is the point. */
  withZone?: boolean
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A timestamp written without a zone: "2026-10-26T20:55:16" or "2026-10-26 20:55". */
const ZONELESS = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

/**
 * The instant a server timestamp names.
 *
 * Every time the API sends is UTC. Postgres sends it with an offset; a database
 * that drops the zone (SQLite, a naive column) sends "2026-10-26T20:55:16", and
 * `new Date()` reads that as the BROWSER's local time, which puts a report that
 * ran this morning hours into the future. A timestamp with no zone is UTC here.
 */
export function parseInstant(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.valueOf()) ? null : value
  const text = String(value).trim()
  const date = new Date(ZONELESS.test(text) ? `${text.replace(' ', 'T')}Z` : text)
  return Number.isNaN(date.valueOf()) ? null : date
}

const toDate = parseInstant

/** Orders two server timestamps by the moment they name, oldest first; a missing one sorts before every real one. */
export function compareInstants(a: string | Date | null | undefined, b: string | Date | null | undefined): number {
  const left = parseInstant(a)?.valueOf() ?? Number.NEGATIVE_INFINITY
  const right = parseInstant(b)?.valueOf() ?? Number.NEGATIVE_INFINITY
  return left === right ? 0 : left < right ? -1 : 1
}

const zoneCache = new Map<string, string | undefined>()

/**
 * A zone name `Intl` will accept, or undefined for the browser's own.
 *
 * A time zone that `Intl` does not know throws a RangeError from the formatter, and one stored
 * schedule with a name this browser cannot read must not take the whole page down with it.
 */
function safeZone(timeZone: string | undefined): string | undefined {
  if (!timeZone) return undefined
  if (zoneCache.has(timeZone)) return zoneCache.get(timeZone)
  let zone: string | undefined = timeZone
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
  } catch {
    zone = undefined
  }
  zoneCache.set(timeZone, zone)
  return zone
}

type DayParts = { year: number; month: number; day: number }

function dayParts(date: Date, timeZone?: string): DayParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(timeZone),
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date)
  const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value)
  return { year: pick('year'), month: pick('month'), day: pick('day') }
}

/** The calendar day an instant falls on in `timeZone`, as a whole number of days. */
function dayNumber(date: Date, timeZone?: string): number {
  const { year, month, day } = dayParts(date, timeZone)
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000)
}

function clock(date: Date, { timeZone, withZone }: ClockOptions): string {
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone: safeZone(timeZone),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...(withZone ? { timeZoneName: 'short' as const } : {}),
  }).format(date)
  // Newer ICU builds put a narrow no-break space before AM/PM.
  return formatted.replace(/\u202f/g, ' ')
}

function calendar(date: Date, now: Date, timeZone?: string): string {
  const { year, month, day } = dayParts(date, timeZone)
  const base = `${MONTHS[month - 1]} ${day}`
  return year === dayParts(now, timeZone).year ? base : `${base}, ${year}`
}

/**
 * "Today 6:00 AM", "Yesterday 6:00 AM", "Tomorrow 7:00 AM", "Fri 6:00 AM",
 * "Oct 5, 8:00 AM". The day is the day in `timeZone`, so a schedule that runs
 * at 7:00 AM Eastern reads "Tomorrow 7:00 AM EDT" wherever the reader sits.
 */
export function formatWhen(value: string | Date | null | undefined, options: ClockOptions = {}): string {
  const date = toDate(value)
  if (!date) return '—'
  const now = options.now ?? new Date()
  const diff = dayNumber(date, options.timeZone) - dayNumber(now, options.timeZone)
  const time = clock(date, options)
  if (diff === 0) return `Today ${time}`
  if (diff === -1) return `Yesterday ${time}`
  if (diff === 1) return `Tomorrow ${time}`
  if (Math.abs(diff) < 7) {
    const weekday = new Intl.DateTimeFormat('en-US', {
      timeZone: safeZone(options.timeZone),
      weekday: 'short',
    }).format(date)
    return `${weekday} ${time}`
  }
  return `${calendar(date, now, options.timeZone)}, ${time}`
}

/** "Oct 26, 2026" — a day with no clock, for headings and "as of" lines. */
export function formatDay(value: string | Date | null | undefined, options: ClockOptions = {}): string {
  const date = toDate(value)
  if (!date) return '—'
  const { year, month, day } = dayParts(date, options.timeZone)
  return `${MONTHS[month - 1]} ${day}, ${year}`
}

/** "3d ago" / "in 2h" / "just now". */
export function formatAgo(value: string | Date | null | undefined, now: Date = new Date()): string {
  const date = toDate(value)
  if (!date) return '—'
  const minutes = Math.round((now.valueOf() - date.valueOf()) / 60_000)
  const future = minutes < 0
  const abs = Math.abs(minutes)
  const say = (amount: string) => (future ? `in ${amount}` : `${amount} ago`)
  if (abs < 1) return 'just now'
  if (abs < 60) return say(`${abs}m`)
  const hours = Math.round(abs / 60)
  if (hours < 24) return say(`${hours}h`)
  const days = Math.round(hours / 24)
  if (days < 60) return say(`${days}d`)
  return say(`${Math.round(days / 30)}mo`)
}

/** "17 KB", "1.2 MB"; `null` is a file the archive does not hold. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return 'Not kept'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`
}

/** "1 run" / "3 runs". */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`
}

/** A raw failure message, cut to one readable line. */
export function oneLine(message: string | null | undefined, max = 140): string {
  const flat = String(message ?? '')
    .replace(/\s+/g, ' ')
    .trim()
  if (flat.length <= max) return flat
  return `${flat.slice(0, max - 1).trimEnd()}…`
}

/** Ends a clause with exactly one full stop, whatever punctuation it already carried. */
export function endSentence(text: string): string {
  const trimmed = text.replace(/[\s.!?]+$/, '')
  return trimmed ? `${trimmed}.` : ''
}
